import React, { createContext, useContext, useState, useEffect } from 'react';

const darkTheme = {
  bgDeep:       "#07001a",
  bgBase:       "#00000c",
  bgCard:       "linear-gradient(135deg,#3B82F614,#8B5CF60F,#06B6D40A)",
  bgHover:      "#160d30",
  bgItem:       "linear-gradient(135deg,#3B82F60F,#FFFFFF04",
  border:       "rgba(139,92,246,0.18)",
  borderActive: "rgba(139,92,246,0.5)",
  accent:       "#8B5CF6",
  accentDim:    "rgba(139, 92, 246, 0.12)",
  accentBright: "#c4b5fd",
  purpleDim:    "rgba(192, 132, 252, 0.1)",
  pink:         "#e879f9",
  pinkDim:      "rgba(232,121,249,0.13)",
  textPrimary:  "#F8FAFC",
  textSecond:   "#94A3B8",
  purple:       "#C084FC",
  textMuted:    "#3d2f5c",
  green:        "#10B981",
  greenDim:     "rgba(16, 185, 129, 0.1)",
  red:          "#F43F5E",
  redDim:       "rgba(244, 63, 94, 0.1)",
  amber:        "#fbbf24",
  gradMain:     "linear-gradient(135deg, #3B82F6, #8B5CF6)",
  gradBar:      "linear-gradient(90deg, #7c3aed, #a78bfa, #e879f9)",
};

const lightTheme = {
  bgDeep:       "#F0F2F9",      // Le bleu très clair du fond global
  bgBase:       "#FFFFFF",
  bgCard:       "#FFFFFF",
  bgHover:      "#E5E8F0",
  bgItem:       "#f6f8fc",
  border:       "#E5E8F0",      // Bordures très fines et claires
  borderActive: "rgba(99, 102, 241, 0.5)",
  accent:       "#6366F1",      // Indigo/Violet principal (Indigo 500)
  accentDim:    "#EEF2FF",      // Fond des badges (Indigo 50)
  accentBright: "#4F46E5",
  purpleDim:    "#F5F3FF",
  pink:         "#ec4899",
  pinkDim:      "#fce7f3",
  textPrimary:  "#1E293B",      // Ardoise foncé pour les titres
  textSecond:   "#64748B",      // Gris-bleu pour les sous-titres
  purple:       "#8B5CF6",
  purpleDim:    "#F5F3FF",
  textMuted:    "#94A3B8",
  // Couleurs spécifiques des résultats
  green:        "#059669",
  greenDim:     "#ECFDF5",
  red:          "#DC2626",
  redDim:       "#FEF2F2",
  amber:        "#d97706",
  gradMain:     "linear-gradient(135deg, #6366F1, #A855F7)",
  gradBar:      "linear-gradient(90deg, #6366F1, #8B5CF6, #ec4899)",
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  // Try reading from localStorage, default to dark
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem('theme');
      if (stored) return stored === 'dark';
    } catch (e) {
      console.error(e);
    }
    return true; // Default to dark theme
  });

  const toggleTheme = () => {
    setIsDark(prev => {
      const newTheme = !prev;
      try {
        localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      } catch (e) {}
      return newTheme;
    });
  };

  const currentTheme = isDark ? darkTheme : lightTheme;

  // Dynamically set CSS custom variables for seamless theme switching
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    Object.entries(currentTheme).forEach(([key, value]) => {
      const cssKey = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssKey, value);
    });
  }, [isDark, currentTheme]);

  return (
    <ThemeContext.Provider value={{ C: currentTheme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
